import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, FileText, GripVertical, ArrowUpDown, Filter } from 'lucide-react';

// Mohalla order for sorting
const MOHALLA_ORDER = ["QUTBI MOHALLA", "NAJMI MOHALLA", "FAKHRI MOHALLA", "FATEMI MOHALLA", "HASANI MOHALLA", "HASHMI MOHALLA"];

const getMohallaSortKey = (mohallaName) => {
  const idx = MOHALLA_ORDER.indexOf((mohallaName || "").toUpperCase());
  return idx >= 0 ? idx : MOHALLA_ORDER.length;
};

const BASE_FIELDS = [
  { id: 'sr_no', label: 'Sr No', default: true },
  { id: 'its_id', label: 'ITS ID', default: true },
  { id: 'member_name', label: 'Member Name', default: true },
  { id: 'mobile', label: 'Mobile', default: true },
  { id: 'age', label: 'Age', default: false },
  { id: 'gender', label: 'Gender', default: false },
  { id: 'blood_group', label: 'Blood Group', default: false },
  { id: 'member_type', label: 'HOF/FM', default: false },
  { id: 'misaq_status', label: 'Misaq Status', default: false },
  { id: 'mohalla', label: 'Mohalla', default: true },
  { id: 'sector', label: 'Sector', default: true },
  // New fields
  { id: 'title', label: 'Title', default: false },
  { id: 'organization', label: 'Organisation', default: false },
  { id: 'qualification', label: 'Qualification', default: false },
  { id: 'email', label: 'Email', default: false },
  { id: 'whatsapp_no', label: 'WhatsApp No', default: false },
  { id: 'marital_status', label: 'Marital Status', default: false },
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const QUALIFICATIONS = ['Primary', 'Secondary', 'Higher Secondary', 'Graduate', 'Post Graduate', 'Doctorate', 'Other'];

export function ListExportDialog({ open, onOpenChange, listType, listData, customColumns, memberCustomData }) {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [format, setFormat] = useState('excel');
  
  // Ensure customColumns and memberCustomData are always arrays/objects
  const safeCustomColumns = useMemo(() => customColumns || [], [customColumns]);
  const safeMemberCustomData = memberCustomData || {};
  const [sortBy, setSortBy] = useState('mohalla'); // mohalla, sector, name, or custom column id
  const [groupBy, setGroupBy] = useState('none'); // none, mohalla, sector
  const [exporting, setExporting] = useState(false);
  
  // Build available fields including custom columns
  const AVAILABLE_FIELDS = useMemo(() => {
    const fields = [...BASE_FIELDS];
    // Add custom columns as available fields
    if (safeCustomColumns && safeCustomColumns.length > 0) {
      safeCustomColumns.forEach(col => {
        fields.push({
          id: `custom_${col.id}`,
          label: col.name,
          default: true,
          isCustom: true,
          customColumnId: col.id
        });
      });
    }
    return fields;
  }, [safeCustomColumns]);

  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldOrder, setFieldOrder] = useState([]);
  
  // Filter states
  const [genderFilter, setGenderFilter] = useState('all');
  const [misaqFilter, setMisaqFilter] = useState('all');
  const [bloodGroupFilter, setBloodGroupFilter] = useState('all');
  const [memberTypeFilter, setMemberTypeFilter] = useState('all'); // all, hof, fm
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  // New filter states
  const [qualificationFilter, setQualificationFilter] = useState('all');
  const [organizationFilter, setOrganizationFilter] = useState('');

  // Get unique organizations from data
  const uniqueOrganizations = useMemo(() => {
    if (!listData || !Array.isArray(listData)) return [];
    const orgs = new Set();
    listData.forEach(m => {
      if (m.organization && m.organization.trim()) {
        orgs.add(m.organization.trim());
      }
    });
    return Array.from(orgs).sort();
  }, [listData]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setFormat('excel');
      // Include default fields + all custom columns by default
      const availableFields = [...BASE_FIELDS];
      if (safeCustomColumns && safeCustomColumns.length > 0) {
        safeCustomColumns.forEach(col => {
          availableFields.push({
            id: `custom_${col.id}`,
            label: col.name,
            default: true,
            isCustom: true,
            customColumnId: col.id
          });
        });
      }
      setSelectedFields(availableFields.filter(f => f.default).map(f => f.id));
      setFieldOrder(availableFields.map(f => f.id));
      setSortBy('mohalla');
      setGroupBy('none');
      setGenderFilter('all');
      setMisaqFilter('all');
      setBloodGroupFilter('all');
      setMemberTypeFilter('all');
      setMinAge('');
      setMaxAge('');
      setQualificationFilter('all');
      setOrganizationFilter('');
    }
  }, [open, safeCustomColumns]);

  const toggleField = (fieldId) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };

  const moveField = (fieldId, direction) => {
    const currentIndex = fieldOrder.indexOf(fieldId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= fieldOrder.length) return;
    
    const newOrder = [...fieldOrder];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    setFieldOrder(newOrder);
  };

  // Helper to get custom column value for a member
  const getCustomColumnValue = (memberId, columnId) => {
    const memberData = safeMemberCustomData[memberId];
    if (!memberData) return '';
    if (typeof memberData === 'string') {
      // Legacy single value - return for first column only
      return safeCustomColumns[0]?.id === columnId ? memberData : '';
    }
    return memberData[columnId] || '';
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field');
      return;
    }

    try {
      setExporting(true);
      
      // Filter data based on criteria
      let filteredData = [...(listData || [])];
      
      // Gender filter
      if (genderFilter !== 'all') {
        filteredData = filteredData.filter(m => 
          (m.gender || '').toLowerCase() === genderFilter.toLowerCase()
        );
      }
      
      // Misaq filter
      if (misaqFilter !== 'all') {
        filteredData = filteredData.filter(m => 
          (m.misaq_status || '').toLowerCase() === misaqFilter.toLowerCase()
        );
      }
      
      // Blood Group filter
      if (bloodGroupFilter !== 'all') {
        filteredData = filteredData.filter(m => 
          (m.blood_group || '') === bloodGroupFilter
        );
      }
      
      // Member Type filter (HOF/FM)
      if (memberTypeFilter !== 'all') {
        filteredData = filteredData.filter(m => {
          const isHof = m.is_hof || m.relationship === 'Self';
          return memberTypeFilter === 'hof' ? isHof : !isHof;
        });
      }
      
      // Qualification filter
      if (qualificationFilter !== 'all') {
        filteredData = filteredData.filter(m => 
          (m.qualification || '').toLowerCase() === qualificationFilter.toLowerCase()
        );
      }
      
      // Organisation filter
      if (organizationFilter) {
        filteredData = filteredData.filter(m => 
          (m.organization || '') === organizationFilter
        );
      }
      
      // Age filter
      if (minAge) {
        filteredData = filteredData.filter(m => (m.age || 0) >= parseInt(minAge));
      }
      if (maxAge) {
        filteredData = filteredData.filter(m => (m.age || 0) <= parseInt(maxAge));
      }
      
      // Sort data
      if (sortBy === 'mohalla') {
        filteredData.sort((a, b) => {
          const mohallaCompare = getMohallaSortKey(a.mohalla) - getMohallaSortKey(b.mohalla);
          if (mohallaCompare !== 0) return mohallaCompare;
          const sectorCompare = (a.sector || '').localeCompare(b.sector || '');
          if (sectorCompare !== 0) return sectorCompare;
          return (a.full_name || '').localeCompare(b.full_name || '');
        });
      } else if (sortBy === 'sector') {
        filteredData.sort((a, b) => {
          const sectorCompare = (a.sector || '').localeCompare(b.sector || '');
          if (sectorCompare !== 0) return sectorCompare;
          return (a.full_name || '').localeCompare(b.full_name || '');
        });
      } else if (sortBy === 'name') {
        filteredData.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      } else if (sortBy.startsWith('custom_')) {
        // Sort by custom column - detect numeric values for proper sorting
        const columnId = sortBy.replace('custom_', '');
        filteredData.sort((a, b) => {
          const valA = getCustomColumnValue(a.id, columnId) || '';
          const valB = getCustomColumnValue(b.id, columnId) || '';
          // Push empty values to the end
          if (!valA && valB) return 1;
          if (valA && !valB) return -1;
          if (!valA && !valB) return 0;
          // Try numeric comparison first (handles amounts, numbers, currency)
          const numA = parseFloat(String(valA).replace(/[^0-9.-]/g, ''));
          const numB = parseFloat(String(valB).replace(/[^0-9.-]/g, ''));
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB; // Numeric sort (ascending: low to high)
          }
          // Fall back to string comparison for non-numeric values
          return valA.toString().localeCompare(valB.toString());
        });
      }

      // Get ordered fields
      const orderedFields = fieldOrder.filter(f => selectedFields.includes(f));
      
      // Prepare export data with custom columns
      const exportData = filteredData.map((member, idx) => {
        const row = {};
        orderedFields.forEach(field => {
          if (field === 'sr_no') row['Sr No'] = idx + 1;
          else if (field === 'its_id') row['ITS ID'] = member.its_id || '';
          else if (field === 'member_name') row['Member Name'] = member.full_name || member.member_name || '';
          else if (field === 'mobile') row['Mobile'] = member.mobile || '';
          else if (field === 'age') row['Age'] = member.age || '';
          else if (field === 'gender') row['Gender'] = member.gender || '';
          else if (field === 'blood_group') row['Blood Group'] = member.blood_group || '';
          else if (field === 'member_type') row['HOF/FM'] = (member.is_hof || member.relationship === 'Self') ? 'HOF' : 'FM';
          else if (field === 'misaq_status') row['Misaq'] = member.misaq_status || '';
          else if (field === 'mohalla') row['Mohalla'] = member.mohalla || '';
          else if (field === 'sector') row['Sector'] = member.sector || '';
          // New fields
          else if (field === 'title') row['Title'] = member.title || '';
          else if (field === 'organization') row['Organisation'] = member.organization || '';
          else if (field === 'qualification') row['Qualification'] = member.qualification || '';
          else if (field === 'email') row['Email'] = member.email || '';
          else if (field === 'whatsapp_no') row['WhatsApp No'] = member.whatsapp_no || '';
          else if (field === 'marital_status') row['Marital Status'] = member.marital_status || '';
          else if (field.startsWith('custom_')) {
            // Handle custom column
            const columnId = field.replace('custom_', '');
            const columnConfig = safeCustomColumns.find(c => c.id === columnId);
            const columnName = columnConfig?.name || 'Custom';
            row[columnName] = getCustomColumnValue(member.id, columnId);
          }
        });
        return row;
      });

      // Build column names for backend
      const columnNames = orderedFields.map(f => {
        if (f.startsWith('custom_')) {
          const columnId = f.replace('custom_', '');
          const columnConfig = safeCustomColumns.find(c => c.id === columnId);
          return columnConfig?.name || 'Custom';
        }
        const fieldConfig = AVAILABLE_FIELDS.find(af => af.id === f);
        return fieldConfig?.label || f;
      });

      // Build checkbox columns mapping {columnName: displayName}
      const checkboxColumns = {};
      orderedFields.forEach(f => {
        if (f.startsWith('custom_')) {
          const columnId = f.replace('custom_', '');
          const columnConfig = safeCustomColumns.find(c => c.id === columnId);
          if (columnConfig?.type === 'checkbox') {
            checkboxColumns[columnConfig.name] = columnConfig.name;
          }
        }
      });

      // Call backend to generate file
      const response = await api.post('/export/list-custom', {
        data: exportData,
        format: format,
        list_name: listType || 'custom_list',
        columns: columnNames,
        checkbox_columns: Object.keys(checkboxColumns).length > 0 ? checkboxColumns : null
      }, { responseType: 'blob' });

      // Download file - using direct download method for all formats
      const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      
      const filename = `${(listType || 'list').replace(/\s+/g, '_')}_export.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Create download link - direct download for both PDF and Excel
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 150);

      toast.success(`${format.toUpperCase()} exported successfully`);
      onOpenChange(false);
    } catch (error) {
      console.error('Export error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to export: ${errorMsg}`);
    } finally {
      setExporting(false);
    }
  };

  // Build sort options including custom columns
  const sortOptions = [
    { value: 'mohalla', label: 'Mohalla-wise' },
    { value: 'sector', label: 'Sector-wise' },
    { value: 'name', label: 'Name (A-Z)' },
    ...safeCustomColumns.map(col => ({
      value: `custom_${col.id}`,
      label: `${col.name} (${col.type === 'number' || col.type === 'amount' ? 'Low-High' : 'A-Z / Low-High'})`
    }))
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export {listType} List
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6">
            {/* Filter Section */}
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter Criteria
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs">Gender</Label>
                  <Select value={genderFilter} onValueChange={setGenderFilter}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Misaq Status</Label>
                  <Select value={misaqFilter} onValueChange={setMisaqFilter}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                      <SelectItem value="not done">Not Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Min Age</Label>
                  <Input 
                    type="number" 
                    placeholder="0"
                    value={minAge}
                    onChange={(e) => setMinAge(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max Age</Label>
                  <Input 
                    type="number" 
                    placeholder="100"
                    value={maxAge}
                    onChange={(e) => setMaxAge(e.target.value)}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Blood Group</Label>
                  <Select value={bloodGroupFilter} onValueChange={setBloodGroupFilter}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {BLOOD_GROUPS.map(bg => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Member Type</Label>
                  <Select value={memberTypeFilter} onValueChange={setMemberTypeFilter}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="hof">HOF Only</SelectItem>
                      <SelectItem value="fm">Family Members Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* New filters */}
                <div>
                  <Label className="text-xs">Qualification</Label>
                  <Select value={qualificationFilter} onValueChange={setQualificationFilter}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {QUALIFICATIONS.map(q => (
                        <SelectItem key={q} value={q}>{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {uniqueOrganizations.length > 0 && (
                  <div>
                    <Label className="text-xs">Organisation</Label>
                    <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">All</SelectItem>
                        {uniqueOrganizations.map(org => (
                          <SelectItem key={org} value={org}>{org}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Format Selection */}
            <div>
              <Label className="text-sm font-medium">Export Format</Label>
              <RadioGroup value={format} onValueChange={setFormat} className="flex gap-4 mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="excel" id="excel" />
                  <Label htmlFor="excel" className="flex items-center gap-2 cursor-pointer">
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    Excel
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pdf" id="pdf" />
                  <Label htmlFor="pdf" className="flex items-center gap-2 cursor-pointer">
                    <FileText className="w-4 h-4 text-red-600" />
                    PDF (Landscape)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Sort Option - Now with custom columns */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4" />
                Sort By (Alphabetically)
              </Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Column Selection - Including Custom Columns */}
            <div>
              <Label className="text-sm font-medium">Select Columns</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {AVAILABLE_FIELDS.map(field => (
                  <div key={field.id} className={`flex items-center space-x-2 ${field.isCustom ? 'bg-emerald-50 p-1 rounded' : ''}`}>
                    <Checkbox 
                      id={field.id}
                      checked={selectedFields.includes(field.id)}
                      onCheckedChange={() => toggleField(field.id)}
                    />
                    <Label htmlFor={field.id} className={`text-sm cursor-pointer ${field.isCustom ? 'text-emerald-700 font-medium' : ''}`}>
                      {field.label}
                      {field.isCustom && <span className="text-xs ml-1">(Custom)</span>}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full">
              Next: Arrange Columns
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Arrange Column Order (Drag to reorder)</Label>
              <p className="text-xs text-slate-500 mb-2">Use arrows to change order</p>
              <div className="space-y-2 border rounded-lg p-2 bg-slate-50 max-h-64 overflow-y-auto">
                {fieldOrder.filter(f => selectedFields.includes(f)).map((fieldId, idx) => {
                  const field = AVAILABLE_FIELDS.find(f => f.id === fieldId);
                  return (
                    <div key={fieldId} className={`flex items-center gap-2 p-2 bg-white rounded border ${field?.isCustom ? 'border-emerald-300 bg-emerald-50' : ''}`}>
                      <GripVertical className="w-4 h-4 text-slate-400" />
                      <span className={`flex-1 text-sm ${field?.isCustom ? 'text-emerald-700 font-medium' : ''}`}>
                        {idx + 1}. {field?.label}
                        {field?.isCustom && <span className="text-xs ml-1">(Custom)</span>}
                      </span>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => moveField(fieldId, 'up')}
                          disabled={idx === 0}
                        >
                          ↑
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => moveField(fieldId, 'down')}
                          disabled={idx === fieldOrder.filter(f => selectedFields.includes(f)).length - 1}
                        >
                          ↓
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button 
                onClick={handleExport} 
                disabled={exporting}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {exporting ? 'Exporting...' : `Export ${format.toUpperCase()}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
